/**
 * @voltxampere/web — Idle detection state (Sprint 33-2)
 *
 * Kullanıcı 3 dakika edit yapmazsa (no mouse/key/touch/wheel) → idle
 * mode. RAF throttle (Sprint 33-2 ikinci modül) bu signal'ı okuyup
 * fps'i 60 → 10'a düşürür. CPU/batarya tasarruf — özellikle laptop'ta
 * kritik.
 *
 * Architecture:
 *   - `isIdleSignal: signal<boolean>` — RAF throttle'ın okuduğu kapı
 *   - `lastInteractionAtSignal: signal<number>` — son etkileşim ms
 *   - `idleDetectionEnabledSignal: signal<boolean>` — AYARLAR toggle
 *     (default true). Disable edilince signal hiç idle olmaz.
 *
 * Listener throttle: 100 ms record DEBOUNCE — mousemove gibi spam'li
 * event'lerin idle timer'ı sürekli reset etmesini engellemek için
 * record-rate üstünden gating.
 *
 * Boot wiring: `installIdleListeners()` global pencereye 5 listener
 * (mousedown / mousemove / keydown / touchstart / wheel) ekler ve ilk
 * idle timer'ı zamanlar. Idempotent — duplicate boot uyarı verir.
 *
 * Copyright © 2026 360 Studio
 * Proprietary — All rights reserved.
 */

import { signal } from "@preact/signals-core";

/** Default idle threshold (3 dakika, ms). */
export const IDLE_THRESHOLD_MS = 3 * 60 * 1000;

/** Listener throttle window — `record` çağrısı bu kadar sık olabilir. */
export const INTERACTION_RECORD_DEBOUNCE_MS = 100;

/**
 * Whether the system is currently idle. RAF throttle reads this to
 * decide between active fps (60) and idle fps (10).
 */
export const isIdleSignal = signal<boolean>(false);

/**
 * Unix-ms timestamp of the last user interaction. Useful for
 * "you've been idle for X minutes" UI affordances (Aşama 2+).
 */
export const lastInteractionAtSignal = signal<number>(Date.now());

/**
 * Master toggle for idle detection. Default true. When false the
 * idle timer never schedules and `isIdleSignal` stays at the last
 * value (typically false).
 */
export const idleDetectionEnabledSignal = signal<boolean>(true);

let idleTimer: ReturnType<typeof setTimeout> | null = null;
// Sentinel: -Infinity so the first `recordInteraction` call always
// passes the throttle check (Date.now() - (-Infinity) > anything).
let lastRecordAt = Number.NEGATIVE_INFINITY;
let installed = false;
let teardownFns: (() => void)[] = [];

function clearIdleTimer(): void {
  if (idleTimer !== null) {
    clearTimeout(idleTimer);
    idleTimer = null;
  }
}

function scheduleIdleCheck(): void {
  clearIdleTimer();
  if (!idleDetectionEnabledSignal.value) return;
  idleTimer = setTimeout(() => {
    idleTimer = null;
    isIdleSignal.value = true;
  }, IDLE_THRESHOLD_MS);
}

/**
 * Record a user interaction. Throttled by
 * INTERACTION_RECORD_DEBOUNCE_MS so event-spam (mousemove) doesn't
 * burn CPU on signal updates.
 */
export function recordInteraction(): void {
  const now = Date.now();
  if (now - lastRecordAt < INTERACTION_RECORD_DEBOUNCE_MS) return;
  lastRecordAt = now;
  lastInteractionAtSignal.value = now;
  if (isIdleSignal.value) {
    isIdleSignal.value = false;
  }
  scheduleIdleCheck();
}

/**
 * Install global window listeners for the 5 canonical user-gesture
 * events. Idempotent — second call logs a dev warning and is a
 * no-op. Boot path calls this once from main.ts.
 */
export function installIdleListeners(): void {
  if (installed) {
    if (import.meta.env.DEV) {
      console.warn("[VXA idle] installIdleListeners called twice; ignoring");
    }
    return;
  }
  installed = true;
  if (typeof window === "undefined") {
    if (import.meta.env.DEV) {
      console.warn("[VXA idle] window is undefined; idle detection disabled");
    }
    return;
  }
  const passive: AddEventListenerOptions = { passive: true };
  const handler = (): void => {
    recordInteraction();
  };
  window.addEventListener("mousedown", handler, passive);
  window.addEventListener("mousemove", handler, passive);
  window.addEventListener("keydown", handler, passive);
  window.addEventListener("touchstart", handler, passive);
  window.addEventListener("wheel", handler, passive);
  teardownFns = [
    () => {
      window.removeEventListener("mousedown", handler);
    },
    () => {
      window.removeEventListener("mousemove", handler);
    },
    () => {
      window.removeEventListener("keydown", handler);
    },
    () => {
      window.removeEventListener("touchstart", handler);
    },
    () => {
      window.removeEventListener("wheel", handler);
    },
  ];
  scheduleIdleCheck();
}

/**
 * Tear down listeners + clear timer. Production never calls this;
 * tests use it to reset between cases.
 */
export function disposeIdleListeners(): void {
  for (const fn of teardownFns) fn();
  teardownFns = [];
  clearIdleTimer();
  installed = false;
}

/** Test-only — full reset of state + listeners. */
export function __resetIdleForTests(): void {
  disposeIdleListeners();
  isIdleSignal.value = false;
  lastInteractionAtSignal.value = Date.now();
  idleDetectionEnabledSignal.value = true;
  lastRecordAt = Number.NEGATIVE_INFINITY;
}
